import React, { useEffect, useMemo } from "react";

import Masonry from "@mui/lab/Masonry";
import { Skeleton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useOperationDespatchContext } from "../../../../../../../contexts/DespatchContext";
import { useAppState } from "Context/StateContext";
import { MemberCertification } from "Models/members/member.certification";
import { useTranslation } from "react-i18next";
import { ICategory } from "Types";
import { checkAvailability, checkResourceAvailability } from "Util/checkAvailability";
import { CertificationType, ResourceName, ResourceSubGroup, ViewPerspective } from "Util/enums";
import { MemberCertificationStatus } from "Util/enums/members";
import { isShowAction, operationsPermissions } from "Util/permissions";

import { ResourceList } from "../../ResourceList";
import { ResourceEquipmentCard } from "../../ResourceList/components/ResourceEquipmentCard/index";
import { ResourceMemberCard } from "../../ResourceList/components/ResourceMemberCard/index";
import { ResourceStorageBoxCard } from "../../ResourceList/components/ResourceStorageBoxCard/index";
import { ResourceTeamCard } from "../../ResourceList/components/ResourceTeamCard/index";
import { ResourceSectionList } from "../../ResourceSectionList/index";
import { ResourceViewerLayout } from "../../ResourceViewerLayout/index";
import { useDragSystem, DNDFieldsNames, DNDSourceTypes } from "../useDragSystem";
import { ResourceViewerEmptyState } from "../ViewerEmptyState";
import { TaskCard } from "./TaskCard";

type Props = {
  dataPermission: {
    members: boolean;
    teams: boolean;
    tasks: boolean;
    equipments: boolean;
    storageBoxes: boolean;
  };
};

const memberTypes = (memberCertifications?: MemberCertification[]) => {
  const typesSet = new Set<CertificationType>();

  memberCertifications?.forEach((certification) => {
    if (certification.status === MemberCertificationStatus.APPROVED) {
      typesSet.add(certification.type);
    }
  });

  return Array.from(typesSet);
};

export const ResourceTaskViewer = (props: Props) => {
  const { dataPermission } = props;
  const { currentUser, userTimezone } = useAppState();
  const { t: translate } = useTranslation();

  const { handleDrop, removeElementFromDespatch, handleDragStart } = useDragSystem();

  const theme = useTheme();

  const {
    equipments,
    equipmentsCount,
    hasNextEquipmentPage,
    isLoadingEquipments,
    isLoadingNextEquipmentPage,
    fetchNextEquipmentPage,
    storageBoxes,
    teams,
    teamsCount,
    tasksFiltered,
    isFetchingTasks,
    isLoadingTeams,
    usersInTasks,
    equipmentsInTasks,
    storageBoxesCount,
    isLoadingStorageBoxes,
    hasNextStorageBoxPage,
    isLoadingNextStorageBoxPage,
    fetchNextStorageBoxPage,
    members,
    isLoadingMembers,
    membersCount,
    hasNextMemberPage,
    isLoadingNextMemberPage,
    fetchNextMemberPage,
    currentDate: actualDate,
    activeFilters,
    setActiveFilters,
    setViewPerspective,
    setActiveSearches,
    totalItemsPerColumn,
  } = useOperationDespatchContext();

  const lgAndUp = useMediaQuery(theme.breakpoints.up("lg"));
  const xlAndUp = useMediaQuery(theme.breakpoints.up("xl"));

  const isUserEnableToEdit = operationsPermissions({
    subGroup: ResourceSubGroup.DISPATCH,
    resourceName: ResourceName.DISPATCH,
    currentUser,
  })?.edit;

  const membersCategories: ICategory[] = [
    {
      name: translate("belongsTo"),
      filterKey: "belongsTo",
      options: [
        { label: translate("own"), value: "own" },
        { label: translate("subcontractor"), value: "subcontractor" },
      ],
    },
    {
      name: translate("typeOfUser"),
      filterKey: "typeOfUser",
      options: [
        { label: translate("leader"), value: "leader" },
        { label: translate("staff"), value: "staff" },
      ],
    },
    {
      name: translate("isAllocated"),
      filterKey: "isAllocated",
      options: [
        { label: translate("yes"), value: "yes" }, // I know it's shitty but the type check of this model is designed to receive only strings
        { label: translate("no"), value: "no" },
      ],
    },
  ];

  const teamCategories: ICategory[] = [
    {
      name: translate("isAllocated"),
      filterKey: "isAllocated",
      options: [
        { label: translate("yes"), value: "yes" },
        { label: translate("no"), value: "no" },
      ],
    },
  ];

  const equipmentCategories: ICategory[] = [
    {
      name: translate("belongsTo"),
      filterKey: "belongsTo",
      options: [
        { label: translate("own"), value: "own" },
        { label: translate("subcontractor"), value: "subcontractor" },
      ],
    },
    {
      name: translate("isAllocated"),
      filterKey: "isAllocated",
      options: [
        { label: translate("yes"), value: "yes" },
        { label: translate("no"), value: "no" },
      ],
    },
  ];

  const storageBoxCategories: ICategory[] = [
    {
      name: translate("isAllocated"),
      filterKey: "isAllocated",
      options: [
        { label: translate("yes"), value: "yes" },
        { label: translate("no"), value: "no" },
      ],
    },
  ];

  const membersItems = useMemo(() => {
    return members.map((member) => {
      return {
        id: Number(member.id),
        sourceType: member?.userCompany?.leader
          ? DNDSourceTypes.ORDER_LEADER
          : DNDSourceTypes.ORDER_MEMBER,
        isDisabled: !checkAvailability(actualDate, member.availabilities, userTimezone.name)
          .isAvailable,
        payload: {
          iconName: member?.userCompany?.leader ? ("crown" as const) : ("user" as const),
          name: member.fullName!,
          tasks: usersInTasks[member.id!],
          isSubcontractor: !!member.subcontractorId,
          availability: member.availabilities,
          certificationsType: memberTypes(member.certifications),
        },
      };
    });
  }, [usersInTasks, members, actualDate]);

  useEffect(() => {
    setViewPerspective(ViewPerspective.TASK);
  }, []);

  const renderLeftSection = () => {
    return (
      <>
        {isShowAction(currentUser, dataPermission.members) && (
          <ResourceSectionList
            title={translate("members")}
            defaultExpanded
            itemsQuantity={membersCount}
            isLoading={isLoadingMembers}
            loadMore={
              hasNextMemberPage && members.length
                ? { isLoading: isLoadingNextMemberPage, action: fetchNextMemberPage }
                : undefined
            }
            handleSearchChange={(searchValue) => {
              setActiveSearches((prev) => ({ ...prev, memberSearch: searchValue }));
            }}
            isActiveFilterByOptions={true}
            generalActions={{
              handleFilterBy: {
                onChange: (filters) => {
                  setActiveFilters((prev) => ({ ...prev, memberFilters: filters }));
                },
                categories: membersCategories,
              },
            }}
            activeFilters={activeFilters.memberFilters}
            handleActiveFilters={(filters) => {
              setActiveFilters((prev) => ({
                ...prev,
                memberFilters: filters,
              }));
            }}
          >
            <ResourceList
              resourceName={DNDFieldsNames.LIST_MEMBERS}
              indexPrefix={100000}
              emptyMessage={translate("noMemberFound")}
              items={membersItems}
              isDisabled={!isUserEnableToEdit}
              render={(item) => {
                return (
                  <ResourceMemberCard
                    actualDate={actualDate}
                    iconName={item.payload.iconName}
                    name={String(item.payload.name)}
                    tasks={item.payload.tasks}
                    isSubcontractor={item.payload.isSubcontractor}
                    availability={item.payload.availability}
                    certificationsType={item.payload.certificationsType}
                  />
                );
              }}
            />
          </ResourceSectionList>
        )}

        {isShowAction(currentUser, dataPermission.teams) && (
          <ResourceSectionList
            title={translate("teams")}
            itemsQuantity={teamsCount}
            isLoading={isLoadingMembers}
            loadMore={
              hasNextMemberPage && members.length
                ? { isLoading: isLoadingNextMemberPage, action: fetchNextMemberPage }
                : undefined
            }
            handleSearchChange={(searchValue) => {
              setActiveSearches((prev) => ({ ...prev, teamSearch: searchValue }));
            }}
            isActiveFilterByOptions={true}
            generalActions={{
              handleFilterBy: {
                onChange: (filters) => {
                  setActiveFilters((prev) => ({ ...prev, teamsFilters: filters }));
                },
                categories: teamCategories,
              },
            }}
            activeFilters={activeFilters.teamsFilters}
            handleActiveFilters={(filters) => {
              setActiveFilters((prev) => ({
                ...prev,
                teamsFilters: filters,
              }));
            }}
          >
            <ResourceList
              resourceName={DNDFieldsNames.LIST_TEAMS}
              indexPrefix={200000}
              isLoading={isLoadingTeams}
              emptyMessage={translate("noTeamFound")}
              isDisabled={!isUserEnableToEdit}
              items={teams.map((team) => ({
                id: Number(team.id),
                sourceType: DNDSourceTypes.ORDER_TEAM,
                payload: {
                  name: team.name,
                  leader: team.memberLeader,
                  members: team.members,
                  equipments: team.fleets,
                  team,
                },
              }))}
              render={(item) => (
                <ResourceTeamCard
                  name={item.payload.name}
                  teamLeader={item.payload.leader}
                  members={item.payload.members}
                  equipments={item.payload.equipments}
                  team={item.payload.team}
                />
              )}
            />
          </ResourceSectionList>
        )}
      </>
    );
  };

  const renderRightSection = () => {
    return (
      <>
        {isShowAction(currentUser, dataPermission.equipments) && (
          <ResourceSectionList
            title={translate("equipments")}
            itemsQuantity={equipmentsCount}
            isLoading={isLoadingEquipments}
            loadMore={
              hasNextEquipmentPage
                ? { isLoading: isLoadingNextEquipmentPage, action: fetchNextEquipmentPage }
                : undefined
            }
            handleSearchChange={(searchValue) => {
              setActiveSearches((prev) => ({ ...prev, equipmentSearch: searchValue }));
            }}
            generalActions={{
              handleFilterBy: {
                onChange: (filters) => {
                  setActiveFilters((prev) => ({ ...prev, equipmentsFilters: filters }));
                },
                categories: equipmentCategories,
              },
            }}
            activeFilters={activeFilters.equipmentsFilters}
            isActiveFilterByOptions={true}
            handleActiveFilters={(filters) => {
              setActiveFilters((prev) => ({
                ...prev,
                equipmentsFilters: filters,
              }));
            }}
          >
            <ResourceList
              resourceName={DNDFieldsNames.LIST_FLEETS}
              indexPrefix={300000}
              isLoading={isLoadingEquipments}
              emptyMessage={translate("noEquipmentFound")}
              isDisabled={!isUserEnableToEdit}
              items={equipments.map((equipment) => {
                return {
                  id: Number(equipment.id),
                  sourceType: DNDSourceTypes.ORDER_EQUIPMENT,
                  isDisabled: !checkResourceAvailability(
                    actualDate,
                    equipment.availabilities,
                    userTimezone.name
                  ).isAvailable,
                  payload: {
                    isSubcontractor: !!equipment.subcontractorId,
                    availability: equipment.availabilities,
                    equipment,
                    tasks: equipmentsInTasks[equipment.id!],
                  },
                };
              })}
              render={(item) => {
                return (
                  <ResourceEquipmentCard
                    actualDate={actualDate}
                    isSubcontractor={item.payload.isSubcontractor}
                    availabilities={item.payload.availability}
                    equipment={item.payload.equipment}
                    tasks={item.payload.tasks}
                  />
                );
              }}
            />
          </ResourceSectionList>
        )}

        {isShowAction(currentUser, dataPermission.storageBoxes) && (
          <ResourceSectionList
            title={translate("storageBoxes")}
            itemsQuantity={storageBoxesCount}
            isLoading={isLoadingStorageBoxes}
            loadMore={
              hasNextStorageBoxPage
                ? { isLoading: isLoadingNextStorageBoxPage, action: fetchNextStorageBoxPage }
                : undefined
            }
            handleSearchChange={(searchValue) => {
              setActiveSearches((prev) => ({ ...prev, storageBoxSearch: searchValue }));
            }}
            generalActions={{
              handleFilterBy: {
                onChange: (filters) => {
                  setActiveFilters((prev) => ({ ...prev, storageBoxFilters: filters }));
                },
                categories: storageBoxCategories,
              },
            }}
            activeFilters={activeFilters.storageBoxFilters}
            isActiveFilterByOptions={true}
            handleActiveFilters={(filters) => {
              setActiveFilters((prev) => ({
                ...prev,
                storageBoxFilters: filters,
              }));
            }}
          >
            <ResourceList
              resourceName={DNDFieldsNames.LIST_STORAGE_BOXES}
              indexPrefix={400000}
              isLoading={isLoadingStorageBoxes}
              emptyMessage={translate("noStorageBoxFound")}
              isDisabled={!isUserEnableToEdit}
              items={storageBoxes.map((storageBox) => ({
                id: Number(storageBox.id),
                sourceType: DNDSourceTypes.ORDER_STORAGE_BOX,
                payload: {
                  title: storageBox.storageBoxType?.name,
                  text: `#${storageBox.number}`,
                  capacityM3: storageBox.storageBoxType?.capacity,
                  isSingleUse: storageBox.singleUse,
                },
              }))}
              render={(item) => {
                return (
                  <ResourceStorageBoxCard
                    capacityM3={item.payload.capacityM3}
                    isSingleUse={!!item.payload.isSingleUse}
                    title={item.payload.title}
                    text={item.payload.text}
                  />
                );
              }}
            />
          </ResourceSectionList>
        )}
      </>
    );
  };

  const getLoadingItems = () => {
    let lenghtSize: number = 3;

    if (xlAndUp) lenghtSize = 6;

    if (lgAndUp && !xlAndUp) lenghtSize = 4;

    return Array.from({ length: lenghtSize }).map((_element, index) => {
      return (
        <div key={index}>
          <Skeleton variant="rectangular" sx={{ borderRadius: "12px", flex: 1 }} height={384} />
        </div>
      );
    });
  };

  const getItems = () => {
    return tasksFiltered.map((task) => {
      return (
        <div key={task.id}>
          <TaskCard task={task} onRemoveElement={removeElementFromDespatch} />
        </div>
      );
    });
  };

  const renderItems = () => {
    let items;

    if (isFetchingTasks) {
      items = getLoadingItems();
    } else {
      items = getItems();
    }

    if (items.length || isFetchingTasks)
      return (
        <Masonry
          columns={totalItemsPerColumn}
          spacing={2}
          sequential
          sx={{ margin: "0px !important" }}
        >
          {items}
        </Masonry>
      );

    return (
      <ResourceViewerEmptyState
        title="No tasks are registered for today"
        subtitle="All caught up! What about a coffee?"
      />
    );
  };

  return (
    <ResourceViewerLayout
      leftSection={renderLeftSection()}
      rightSection={renderRightSection()}
      onDrop={handleDrop}
      onDragStart={handleDragStart}
    >
      {renderItems()}
    </ResourceViewerLayout>
  );
};
